import React, { useMemo, useCallback } from 'react';
import { SelectWithLabel, SelectOption } from '../ui/InputWithLabel';

export interface RowsPerPageSelectProps {
  disabled?: boolean;
  options: number[];
  testId?: string;
  value: number;
  onChange: (value: number) => void;
}

export const RowsPerPageSelect: React.FC<RowsPerPageSelectProps> = React.memo(({
  value,
  options,
  onChange,
  disabled = false,
  testId = 'rows-per-page-select',
}) => {
  // Convert number options to SelectOption format - memoized to avoid recreation
  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        label: option.toString(),
        value: option,
      })),
    [options]
  );

  // Find current value as SelectOption - memoized
  const selectedOption = useMemo(
    () => selectOptions.find((opt) => opt.value === value) || selectOptions[0],
    [selectOptions, value]
  );

  // Memoized change handler
  const handleChange = useCallback(
    (option: SelectOption | null) => {
      if (option) {
        onChange(option.value);
      }
    },
    [onChange]
  );

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="text-sm font-normal text-primary-700 whitespace-nowrap">
        Rows per page:
      </span>
      <SelectWithLabel
        name="rowsPerPage"
        noLabel={true}
        value={selectedOption}
        options={selectOptions}
        onChange={handleChange}
        disabled={disabled}
        width="80px"
        height="h-8"
        className="!mb-0"
        testId={`${testId}-dropdown`}
      />
    </div>
  );
});

RowsPerPageSelect.displayName = 'RowsPerPageSelect';
